/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.77: This file uses idiomatic React patterns (effect-driven
 * state hydration after fetches, English text with apostrophes /
 * quotes in JSX). The experimental React 19 hooks rules
 * (`set-state-in-effect`, `purity`, `preserve-manual-memoization`)
 * are not shipped by eslint-plugin-react-hooks 5.2.0 — only the
 * standalone Emergent lint tool surfaces them, and they mis-flag
 * these intentional patterns. `react/no-unescaped-entities` similarly
 * flags safe English text.
 */
// Insights — v77.99.36 (Phase 3)
//
// Reports & insights dashboard. Four cards: spend trends, maintenance
// compliance, handover stats, journal mood trend. All pulled in
// parallel from /api/insights/* and rendered with recharts for the
// charts that actually benefit from one. Privacy is enforced server-
// side so the page can render whatever it's handed.

/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.75: standard "fetch on filter change" useEffects with
 * setState in async IIFEs. eslint-plugin-react-hooks v5's
 * set-state-in-effect rule is over-eager for this canonical pattern.
 * Other React-19 lints (rules-of-hooks, exhaustive-deps) stay enabled.
 */

import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useChildScope } from "@/context/ChildScopeContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, Legend, PieChart, Pie, Cell,
} from "recharts";
import { LineChart as LineIcon, BarChart3, Smile, Wallet, ArrowLeftRight, TrendingUp, Inbox, Activity, CalendarCheck, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { money } from "@/lib/format";

const CATEGORY_PALETTE = [
  "#1F9FA4", "#B88A3A", "#7A5AA6", "#C0291D", "#0F5658", "#4A6170",
  "#8C2A22", "#6F4E1E", "#4E3877", "#3A4548",
];

const MOOD_LABEL = { 1: "Very low", 2: "Low", 3: "Neutral", 4: "Good", 5: "Very good" };

// v77.99.75 — Module-level Recharts prop constants. These were inline
// object literals before, which forced a new object reference on every
// render and dirtied Recharts' shouldComponentUpdate checks. Hoisting
// to module scope keeps the same identity across renders.
const CHART_MARGIN = { top: 8, right: 16, left: 0, bottom: 4 };
const CHART_TICK = { fontSize: 10, fill: "#6F828A" };
const CHART_TOOLTIP_STYLE = { fontSize: 12 };
const CHART_DOT = { r: 3 };
const CHART_MOOD_TICKS = [1, 2, 3, 4, 5];
const CHART_MOOD_DOMAIN = [1, 5];

function pctClass(pct) {
  if (pct >= 90) return "text-[#0F5658]";
  if (pct >= 70) return "text-[#6F4E1E]";
  return "text-[#8C2A22]";
}

function fmtMonth(key) {
  // "2026-05" → "May 2026"
  try {
    const [y, m] = key.split("-");
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
  } catch { return key; }
}

function fmtWeek(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  } catch { return iso; }
}

export default function Insights() {
  const { user, members, currency } = useAuth();
  const { selectedChildId } = useChildScope();
  const [expenseMonths, setExpenseMonths] = useState(12);
  const [maintWindow, setMaintWindow] = useState(90);
  const [handoverWindow, setHandoverWindow] = useState(90);
  const [moodWeeks, setMoodWeeks] = useState(12);
  // v77.99.57 — Summary strip + request outcomes window. Drives a
  // shared `Last 90 days` selector at the top of the page so the
  // strip stays in sync with the requests card. Defaults to 90.
  const [summaryWindow, setSummaryWindow] = useState(90);
  const [data, setData] = useState({ exp: null, maint: null, handover: null, mood: null, summary: null, requests: null, reimburse: null, communication: null, observations: null, loading: true });

  // v77.99.60 — Persist the collapsed state of the Observed patterns
  // card. Defaults to expanded the first time a user lands on the
  // Insights page; once collapsed the preference sticks.
  const OBS_COLLAPSE_KEY = "cpc_insights_observations_collapsed";
  const [observationsCollapsed, setObservationsCollapsedState] = useState(() => {
    try { return localStorage.getItem(OBS_COLLAPSE_KEY) === "1"; } catch (e) { console.debug("[insights] localStorage read failed:", e?.message); return false; }
  });
  const setObservationsCollapsed = (v) => {
    setObservationsCollapsedState(v);
    try { localStorage.setItem(OBS_COLLAPSE_KEY, v ? "1" : "0"); } catch (e) { console.debug("[insights] localStorage write failed:", e?.message); }
  };

  useEffect(() => {
    let cancelled = false;
    setData((s) => ({ ...s, loading: true }));
    const expQ = new URLSearchParams({ months: String(expenseMonths) });
    if (selectedChildId) expQ.set("child_id", selectedChildId);
    const moodQ = new URLSearchParams({ weeks: String(moodWeeks) });
    if (selectedChildId) moodQ.set("child_id", selectedChildId);
    const sumQ = new URLSearchParams({ window_days: String(summaryWindow) });
    if (selectedChildId) sumQ.set("child_id", selectedChildId);
    Promise.allSettled([
      api.get(`/insights/expenses?${expQ.toString()}`),
      api.get(`/insights/maintenance-compliance?window_days=${maintWindow}`),
      api.get(`/insights/handovers?window_days=${handoverWindow}`),
      api.get(`/insights/mood?${moodQ.toString()}`),
      api.get(`/insights/summary?${sumQ.toString()}`),
      api.get(`/insights/requests?window_days=${summaryWindow}`),
      api.get(`/insights/reimbursement?months=${expenseMonths}${selectedChildId ? `&child_id=${selectedChildId}` : ""}`),
      api.get(`/insights/communication?window_days=${summaryWindow}`),
      api.get(`/insights/observations?window_days=${summaryWindow}`),
    ]).then((results) => {
      if (cancelled) return;
      const [e, m, h, mo, s, rq, rb, comm, obs] = results;
      setData({
        exp: e.status === "fulfilled" ? e.value.data : null,
        maint: m.status === "fulfilled" ? m.value.data : null,
        handover: h.status === "fulfilled" ? h.value.data : null,
        mood: mo.status === "fulfilled" ? mo.value.data : null,
        summary: s.status === "fulfilled" ? s.value.data : null,
        requests: rq.status === "fulfilled" ? rq.value.data : null,
        reimburse: rb.status === "fulfilled" ? rb.value.data : null,
        communication: comm.status === "fulfilled" ? comm.value.data : null,
        observations: obs.status === "fulfilled" ? obs.value.data : null,
        loading: false,
      });
    });
    return () => { cancelled = true; };
  }, [expenseMonths, maintWindow, handoverWindow, moodWeeks, selectedChildId, summaryWindow]);

  const memberName = useMemo(() => {
    const map = new Map((members || []).map((m) => [m.id, m.name]));
    return (id) => map.get(id) || "—";
  }, [members]);

  // v77.99.37 — Active child filter badge. Renders when a specific
  // child is selected in the global Child Switcher and the card's
  // data is therefore filtered. Helps users explain why an Insights
  // total differs from the unfiltered view. We pull the child name
  // from useChildScope().children rather than re-fetching.
  const { children: scopeChildren } = useChildScope();
  const activeChildName = useMemo(() => {
    if (!selectedChildId) return null;
    const c = (scopeChildren || []).find((x) => x.id === selectedChildId);
    return c?.name || null;
  }, [selectedChildId, scopeChildren]);
  const ChildScopeBadge = activeChildName ? (
    <span
      // v77.99.57 — Toned down: soft sand tint, low-contrast border,
      // sentence-case text (no SCREAMING uppercase tracking). Keeps
      // the charts as the primary visual focus.
      className="text-[11px] px-2 py-0.5 border rounded-sm bg-[#F5F1EA] text-[#6F4E1E] border-[#EADFC9] inline-flex items-center gap-1 flex-shrink-0"
      data-testid="insights-child-scope-badge"
      title={`Showing only data for ${activeChildName}. Switch back to "All children" in the top bar to widen the view.`}
    >
      <span className="opacity-70">Filtered:</span> {activeChildName}
    </span>
  ) : null;

  return (
    <div className="space-y-6 min-w-0 max-w-full" data-testid="insights-page">
      <div>
        <div className="text-xs uppercase tracking-[0.12em] text-[#6F828A]">Reports & insights</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-medium mt-1 inline-flex items-center gap-2 flex-wrap">
          <TrendingUp className="w-6 h-6 text-[#116B6E]" strokeWidth={1.6} />
          Patterns at a glance
          {ChildScopeBadge}
        </h1>
        <p className="text-sm text-[#6F828A] mt-1">
          Quiet, child-scoped aggregations across spend, maintenance, calendar handovers and journal mood. Numbers only — never raw entries.
        </p>
      </div>

      {/* v77.99.57 — Compact "Insight summary" strip. 5 stat cards in
          a horizontal-scroll row on mobile, a clean grid on desktop.
          Each tile is intentionally minimal: tiny uppercase label,
          big tabular number, no decorations. Window selector is
          shared with the Requests card below so the two surfaces
          stay in sync. */}
      <Card data-testid="insights-summary-strip" className="border-[#D9E1E3] rounded-md shadow-none overflow-hidden min-w-0 max-w-full">
        <CardContent className="p-3 sm:p-4 max-w-full min-w-0 overflow-hidden">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="text-[10px] uppercase tracking-[0.12em] text-[#6F828A]">Insight summary</div>
            <Select value={String(summaryWindow)} onValueChange={(v) => setSummaryWindow(Number(v))}>
              <SelectTrigger className="w-[140px] h-7 text-xs" data-testid="insights-summary-window"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="180">Last 180 days</SelectItem>
                <SelectItem value="365">Last 12 months</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Horizontal scroll on mobile, equal-width grid on tablet+.
              `overflow-x-auto` is scoped to this row only; the outer
              Card has `overflow-hidden` so the scroll never leaks
              into the page width. */}
          <div className="flex sm:grid sm:grid-cols-3 lg:grid-cols-5 gap-2.5 overflow-x-auto pb-1 sm:overflow-visible snap-x snap-mandatory sm:snap-none max-w-full">
            <StatTile
              label="Maintenance on time"
              value={data.summary?.maintenance_on_time_pct == null ? "—" : `${data.summary.maintenance_on_time_pct}%`}
              hint={data.summary?.maintenance_scheduled ? `of ${data.summary.maintenance_scheduled} scheduled` : "no scheduled"}
              testid="stat-maintenance-on-time"
            />
            <StatTile
              label="Schedule consistency"
              value={data.summary?.schedule_consistency_pct == null ? "—" : `${data.summary.schedule_consistency_pct}%`}
              hint={data.summary?.schedule_changes_count ? `${data.summary.schedule_changes_count} changes` : "no changes"}
              testid="stat-schedule-consistency"
            />
            <StatTile
              label="Requests responded to"
              value={data.summary?.requests_response_rate_pct == null ? "—" : `${data.summary.requests_response_rate_pct}%`}
              hint={data.summary?.avg_response_hours != null ? `avg ${data.summary.avg_response_hours}h` : "no decisions yet"}
              testid="stat-requests-responded"
            />
            <StatTile
              label="Avg response time"
              value={data.summary?.avg_response_hours == null ? "—" : `${data.summary.avg_response_hours}h`}
              hint="requests"
              testid="stat-avg-response-time"
            />
            <StatTile
              label="Shared expenses"
              value={data.summary?.shared_expenses_total == null ? "—" : money(data.summary.shared_expenses_total, data.summary.currency || currency)}
              hint="logged in window"
              testid="stat-shared-expenses"
            />
          </div>
        </CardContent>
      </Card>

      {/* ----- v77.99.58 — Observed patterns (deterministic, rule-based) ----- */}
      {Array.isArray(data.observations?.observations) && data.observations.observations.length > 0 && (
        <Card data-testid="insights-card-observations" style={{ borderTop: "3px solid #8F6B1F" }} className="border-[#D9E1E3] rounded-md shadow-none">
          <CardHeader className="border-b border-[#D9E1E3] bg-[#FDFBF6] p-0">
            <button
              type="button"
              onClick={() => setObservationsCollapsed(!observationsCollapsed)}
              className="w-full text-left flex items-center justify-between gap-3 px-6 py-4 hover:bg-[#F8F3E8] transition-colors rounded-t-md"
              data-testid="insights-observations-toggle"
              aria-expanded={!observationsCollapsed}
              aria-controls="insights-observations-body"
            >
              <CardTitle className="font-heading text-base inline-flex items-center gap-2 min-w-0">
                <Sparkles className="w-4 h-4 text-[#8F6B1F] flex-shrink-0" strokeWidth={1.6} /> Observed patterns
                <span
                  className="text-[10px] uppercase tracking-[0.1em] text-[#8F6B1F] bg-[#F5EAD2] border border-[#E8D6A8] px-1.5 py-0.5 rounded-sm ml-1 tabular-nums"
                  data-testid="insights-observations-count"
                >
                  {data.observations.observations.length}
                </span>
              </CardTitle>
              <span
                className="inline-flex items-center text-xs text-[#6F828A] gap-1 flex-shrink-0"
                data-testid="insights-observations-toggle-label"
              >
                {observationsCollapsed ? (
                  <>Show <ChevronDown className="w-4 h-4" strokeWidth={1.8} /></>
                ) : (
                  <>Hide <ChevronUp className="w-4 h-4" strokeWidth={1.8} /></>
                )}
              </span>
            </button>
          </CardHeader>
          {!observationsCollapsed && (
            <CardContent className="p-4" id="insights-observations-body">
              <p className="text-sm text-[#4A5A60] mb-3 leading-relaxed">
                Neutral observations highlighting changes and trends across the selected time period.
              </p>
              <ul className="space-y-1.5 text-sm" data-testid="insights-observations-list">
                {data.observations.observations.map((o) => (
                  <li
                    key={o.id}
                    className="flex items-start gap-2 text-[#2E3A3F]"
                    data-testid={`insights-observation-${o.type}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#8F6B1F] flex-shrink-0 mt-2" />
                    <span className="flex-1 min-w-0 break-words">{o.text}</span>
                  </li>
                ))}
              </ul>
              <p
                className="text-[10px] text-[#9AA8AD] mt-4 leading-relaxed"
                data-testid="insights-observations-footnote"
              >
                Generated from structured app activity only. No message or journal content is analysed.
              </p>
            </CardContent>
          )}
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-5 min-w-0 max-w-full">
        {/* ----- Spend trend ----- */}
        <Card data-testid="insights-card-expenses" style={{ borderTop: "3px solid #1F9FA4" }} className="border-[#D9E1E3] rounded-md shadow-none">
          <CardHeader className="border-b border-[#D9E1E3] flex flex-row items-center justify-between bg-[#F4FAFA]">
            <CardTitle className="font-heading text-base inline-flex items-center gap-2">
              <Wallet className="w-4 h-4 text-[#116B6E]" strokeWidth={1.6} /> Spend trend
              {ChildScopeBadge}
            </CardTitle>
            <Select value={String(expenseMonths)} onValueChange={(v) => setExpenseMonths(Number(v))}>
              <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="insights-expenses-window"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="6">Last 6 months</SelectItem>
                <SelectItem value="12">Last 12 months</SelectItem>
                <SelectItem value="24">Last 24 months</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {!data.exp ? (
              <div className="text-sm text-[#6F828A]">{data.loading ? "Loading…" : "Nothing logged in this window yet."}</div>
            ) : (
              <>
                <div className="text-xs text-[#6F828A]" data-testid="insights-expenses-total">
                  Total {expenseMonths}m: <span className="font-medium text-[#0B2E32]">{money(data.exp.total, data.exp.currency || currency)}</span>
                </div>
                <div className="h-[200px]">
                  <ResponsiveContainer>
                    <LineChart data={data.exp.series} margin={CHART_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EEF3F4" />
                      <XAxis dataKey="month" tick={CHART_TICK} tickFormatter={fmtMonth} />
                      <YAxis tick={CHART_TICK} />
                      <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelFormatter={fmtMonth} formatter={(v) => money(v, data.exp.currency || currency)} />
                      <Line type="monotone" dataKey="total" stroke="#1F9FA4" strokeWidth={2} dot={CHART_DOT} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="text-xs text-[#6F828A] mb-1">Top categories</div>
                <ul className="space-y-1" data-testid="insights-expenses-categories">
                  {Object.entries(data.exp.category_totals).slice(0, 6).map(([cat, total], i) => {
                    const pct = data.exp.total ? Math.round((total / data.exp.total) * 100) : 0;
                    return (
                      <li key={cat} className="flex items-center gap-2 text-xs" data-testid={`insights-category-${cat}`}>
                        <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: CATEGORY_PALETTE[i % CATEGORY_PALETTE.length] }} />
                        <span className="capitalize text-[#2E3A3F] w-28 truncate">{cat}</span>
                        <div className="flex-1 h-2 bg-[#EEF3F4] rounded-full overflow-hidden">
                          <div className="h-full" style={{ width: `${pct}%`, background: CATEGORY_PALETTE[i % CATEGORY_PALETTE.length] }} />
                        </div>
                        <span className="tabular-nums text-[#0B2E32] w-24 text-right">{money(total, data.exp.currency || currency)}</span>
                        <span className="tabular-nums text-[#6F828A] w-10 text-right">{pct}%</span>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </CardContent>
        </Card>

        {/* ----- Maintenance compliance ----- */}
        <Card data-testid="insights-card-maintenance" style={{ borderTop: "3px solid #B88A3A" }} className="border-[#D9E1E3] rounded-md shadow-none">
          <CardHeader className="border-b border-[#D9E1E3] flex flex-row items-center justify-between bg-[#FDFBF6]">
            <CardTitle className="font-heading text-base inline-flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-[#6F4E1E]" strokeWidth={1.6} /> Maintenance compliance
            </CardTitle>
            <Select value={String(maintWindow)} onValueChange={(v) => setMaintWindow(Number(v))}>
              <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="insights-maintenance-window"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="180">Last 180 days</SelectItem>
                <SelectItem value="365">Last 12 months</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {!data.maint ? (
              <div className="text-sm text-[#6F828A]">{data.loading ? "Loading…" : "Nothing logged in this window yet."}</div>
            ) : data.maint.overall.scheduled === 0 ? (
              <div className="text-sm text-[#6F828A]" data-testid="insights-maintenance-empty">
                No maintenance arrangements have payments scheduled in this window.
              </div>
            ) : (
              <>
                {/* v77.99.92 — When there are 2+ co-parents, the
                    "overall" number conflates relationships that may
                    have very different compliance profiles. Switch to
                    a per-co-parent breakdown so a parent who has, say,
                    one rock-solid arrangement and one chronic-arrears
                    arrangement sees both stories, not a misleading
                    average. Single-co-parent households see the
                    classic overall stat unchanged. */}
                {data.maint.by_coparent && data.maint.by_coparent.length >= 2 ? (
                  <div className="space-y-2.5" data-testid="insights-maintenance-by-coparent">
                    <div className="text-xs text-[#6F828A]">
                      On-time compliance per co-parent · {data.maint.overall.scheduled} scheduled across {data.maint.by_coparent.length} co-parents
                    </div>
                    {data.maint.by_coparent.map((cp) => (
                      <div
                        key={cp.coparent_id}
                        className="border border-[#EFE3CB] rounded-md p-2.5 bg-[#FFFDF7]"
                        data-testid={`insights-maintenance-coparent-${cp.coparent_id}`}
                      >
                        <div className="flex items-baseline gap-3">
                          <span className="text-sm font-medium text-[#2E3A3F] truncate flex-1 min-w-0">
                            {cp.coparent_name}
                          </span>
                          <span className={`font-heading text-xl font-semibold tabular-nums ${pctClass(cp.compliance_pct)}`}>
                            {cp.compliance_pct}%
                          </span>
                        </div>
                        <div className="text-[11px] text-[#6F828A] mt-0.5">
                          {cp.arrangement_count} arrangement{cp.arrangement_count === 1 ? "" : "s"} · {cp.scheduled} scheduled
                          {cp.paid > 0 && ` · ${cp.paid} on time`}
                          {cp.paid_late > 0 && ` · ${cp.paid_late} paid late`}
                          {cp.late > 0 && ` · ${cp.late} overdue`}
                          {cp.missed > 0 && ` · ${cp.missed} missed`}
                          {cp.part_paid > 0 && ` · ${cp.part_paid} part-paid`}
                          {cp.waived > 0 && ` · ${cp.waived} waived`}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="flex items-baseline gap-3" data-testid="insights-maintenance-overall">
                      <span className={`font-heading text-3xl font-semibold tabular-nums ${pctClass(data.maint.overall.compliance_pct)}`}>
                        {data.maint.overall.compliance_pct}%
                      </span>
                      <span className="text-xs text-[#6F828A]">on-time over {data.maint.overall.scheduled} scheduled payments</span>
                    </div>
                    <div className="text-xs text-[#6F828A]">
                      {data.maint.overall.paid} paid on time
                      {data.maint.overall.paid_late > 0 && ` · ${data.maint.overall.paid_late} paid late`}
                      {data.maint.overall.late > 0 && ` · ${data.maint.overall.late} overdue`}
                      {data.maint.overall.missed > 0 && ` · ${data.maint.overall.missed} missed`}
                      {data.maint.overall.part_paid > 0 && ` · ${data.maint.overall.part_paid} part-paid`}
                      {data.maint.overall.waived > 0 && ` · ${data.maint.overall.waived} waived`}
                    </div>
                  </>
                )}
                <ul className="space-y-1.5 pt-1" data-testid="insights-maintenance-arrangements">
                  {data.maint.arrangements.slice(0, 6).map((a) => (
                    <li key={a.arrangement_id} className="flex items-center gap-2 text-xs" data-testid={`insights-maintenance-row-${a.arrangement_id}`}>
                      <span className="flex-1 min-w-0 truncate text-[#2E3A3F]">
                        {memberName(a.paying_parent_id)} → {memberName(a.receiving_parent_id)}
                        <span className="text-[#6F828A]"> · {a.counts.scheduled} scheduled</span>
                      </span>
                      <div className="w-20 h-2 bg-[#FBEFED] rounded-full overflow-hidden">
                        <div className="h-full bg-[#0F5658]" style={{ width: `${a.compliance_pct}%` }} />
                      </div>
                      <span className={`tabular-nums w-12 text-right font-medium ${pctClass(a.compliance_pct)}`}>{a.compliance_pct}%</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </CardContent>
        </Card>

        {/* ----- Handover stats ----- */}
        <Card data-testid="insights-card-handovers" style={{ borderTop: "3px solid #7A5AA6" }} className="border-[#D9E1E3] rounded-md shadow-none">
          <CardHeader className="border-b border-[#D9E1E3] flex flex-row items-center justify-between bg-[#FAF8FD]">
            <CardTitle className="font-heading text-base inline-flex items-center gap-2">
              <ArrowLeftRight className="w-4 h-4 text-[#4E3877]" strokeWidth={1.6} /> Calendar handovers
            </CardTitle>
            <Select value={String(handoverWindow)} onValueChange={(v) => setHandoverWindow(Number(v))}>
              <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="insights-handovers-window"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="180">Last 180 days</SelectItem>
                <SelectItem value="365">Last 12 months</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {!data.handover ? (
              <div className="text-sm text-[#6F828A]">{data.loading ? "Loading…" : "Nothing logged in this window yet."}</div>
            ) : data.handover.totals.events === 0 && data.handover.totals.overrides === 0 ? (
              <div className="text-sm text-[#6F828A]" data-testid="insights-handovers-empty">
                No pickup/dropoff events or custody overrides logged in this window.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid="insights-handovers-totals">
                  <div className="border border-[#EEF3F4] rounded-sm p-2">
                    <div className="uppercase tracking-[0.1em] text-[10px] text-[#6F828A]">Pickups</div>
                    <div className="font-heading text-lg font-semibold tabular-nums">{data.handover.totals.pickup}</div>
                  </div>
                  <div className="border border-[#EEF3F4] rounded-sm p-2">
                    <div className="uppercase tracking-[0.1em] text-[10px] text-[#6F828A]">Dropoffs</div>
                    <div className="font-heading text-lg font-semibold tabular-nums">{data.handover.totals.dropoff}</div>
                  </div>
                  <div className="border border-[#EEF3F4] rounded-sm p-2">
                    <div className="uppercase tracking-[0.1em] text-[10px] text-[#6F828A]">Overrides</div>
                    <div className="font-heading text-lg font-semibold tabular-nums">{data.handover.totals.overrides}</div>
                  </div>
                  <div className="border border-[#EEF3F4] rounded-sm p-2">
                    <div className="uppercase tracking-[0.1em] text-[10px] text-[#6F828A]">Cancelled</div>
                    <div className="font-heading text-lg font-semibold tabular-nums">{data.handover.totals.cancelled}</div>
                  </div>
                </div>
                {data.handover.by_parent.length > 0 && (
                  <div className="h-[180px] mt-2">
                    <ResponsiveContainer>
                      <BarChart data={data.handover.by_parent} margin={CHART_MARGIN}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EEF3F4" />
                        <XAxis dataKey="name" tick={CHART_TICK} />
                        <YAxis tick={CHART_TICK} allowDecimals={false} />
                        <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="pickup" fill="#7A5AA6" name="Pickups" />
                        <Bar dataKey="dropoff" fill="#B88A3A" name="Dropoffs" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {/* v77.99.93 — Per-co-parent friction breakdown.
                    Renders when there are 2+ co-parents on the
                    response so the user can spot which RELATIONSHIP
                    has the cancellations + overrides (vs. a conflated
                    "household average"). Single-co-parent households
                    skip this block entirely. */}
                {Array.isArray(data.handover.by_coparent) && data.handover.by_coparent.length >= 2 && (
                  <div className="space-y-2 pt-2 border-t border-[#EFE3CB] mt-2" data-testid="insights-handovers-by-coparent">
                    <div className="text-xs text-[#6F828A]">
                      Friction per co-parent · cancellations + overrides ÷ all handovers in window
                    </div>
                    {data.handover.by_coparent.map((cp) => {
                      const friction = cp.cancelled + cp.overrides;
                      // High-friction threshold: ≥15% of all handovers
                      // were cancellations/overrides — flags the
                      // relationship that's eating most of the energy.
                      const high = cp.friction_pct >= 15 && friction > 0;
                      return (
                        <div
                          key={cp.coparent_id}
                          className={`border rounded-md p-2.5 ${high ? "border-[#E0B7B5] bg-[#FBF1F0]" : "border-[#EFE3CB] bg-[#FFFDF7]"}`}
                          data-testid={`insights-handovers-coparent-${cp.coparent_id}`}
                        >
                          <div className="flex items-baseline gap-3">
                            <span className="text-sm font-medium text-[#2E3A3F] truncate flex-1 min-w-0">
                              {cp.coparent_name}
                            </span>
                            <span className={`font-heading text-lg font-semibold tabular-nums ${high ? "text-[#A03C2F]" : "text-[#4E3877]"}`}>
                              {cp.friction_pct}%
                            </span>
                          </div>
                          <div className="text-[11px] text-[#6F828A] mt-0.5">
                            {cp.events} completed
                            {cp.pickup > 0 && ` · ${cp.pickup} pickup${cp.pickup === 1 ? "" : "s"}`}
                            {cp.dropoff > 0 && ` · ${cp.dropoff} dropoff${cp.dropoff === 1 ? "" : "s"}`}
                            {cp.cancelled > 0 && ` · ${cp.cancelled} cancelled`}
                            {cp.overrides > 0 && ` · ${cp.overrides} override${cp.overrides === 1 ? "" : "s"}`}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* ----- Mood trend ----- */}
        <Card data-testid="insights-card-mood" style={{ borderTop: "3px solid #0F5658" }} className="border-[#D9E1E3] rounded-md shadow-none">
          <CardHeader className="border-b border-[#D9E1E3] flex flex-row items-center justify-between bg-[#F4FAFA]">
            <CardTitle className="font-heading text-base inline-flex items-center gap-2">
              <Smile className="w-4 h-4 text-[#116B6E]" strokeWidth={1.6} /> Journal mood trend
              {ChildScopeBadge}
            </CardTitle>
            <Select value={String(moodWeeks)} onValueChange={(v) => setMoodWeeks(Number(v))}>
              <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="insights-mood-window"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="4">Last 4 weeks</SelectItem>
                <SelectItem value="8">Last 8 weeks</SelectItem>
                <SelectItem value="12">Last 12 weeks</SelectItem>
                <SelectItem value="26">Last 26 weeks</SelectItem>
                <SelectItem value="52">Last year</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {!data.mood ? (
              <div className="text-sm text-[#6F828A]">{data.loading ? "Loading…" : "Nothing logged in this window yet."}</div>
            ) : data.mood.moods_recorded === 0 ? (
              <div className="text-sm text-[#6F828A]" data-testid="insights-mood-empty">
                No mood recorded in any journal entry yet. Pick a mood when you create or log a journal entry to see this trend.
              </div>
            ) : (
              <>
                <div className="flex items-baseline gap-3" data-testid="insights-mood-overall">
                  <span className="font-heading text-3xl font-semibold tabular-nums text-[#0F5658]">{data.mood.overall_avg ?? "—"}</span>
                  <span className="text-xs text-[#6F828A]">
                    average over {data.mood.moods_recorded} mood{data.mood.moods_recorded === 1 ? "" : "s"} ·
                    {" "}{data.mood.entries_total} entries
                  </span>
                </div>
                <div className="h-[200px]">
                  <ResponsiveContainer>
                    <LineChart data={data.mood.series} margin={CHART_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EEF3F4" />
                      <XAxis dataKey="week_start" tick={CHART_TICK} tickFormatter={fmtWeek} />
                      <YAxis domain={CHART_MOOD_DOMAIN} ticks={CHART_MOOD_TICKS} tick={CHART_TICK} tickFormatter={(v) => MOOD_LABEL[v] || v} />
                      <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelFormatter={fmtWeek} formatter={(v) => v != null ? `${v} (${MOOD_LABEL[Math.round(v)] || ""})` : "—"} />
                      <Line type="monotone" dataKey="avg_mood" stroke="#0F5658" strokeWidth={2} dot={CHART_DOT} connectNulls={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                {data.mood.top_tags?.length > 0 && (
                  <div data-testid="insights-mood-tags">
                    <div className="text-xs text-[#6F828A] mb-1">Most-used tags</div>
                    <div className="flex flex-wrap gap-1.5">
                      {data.mood.top_tags.map(([t, n]) => (
                        <span key={t} className="text-[10px] tracking-[0.05em] px-2 py-0.5 border rounded-full bg-white border-[#D9E1E3] text-[#4A6170] capitalize" data-testid={`insights-mood-tag-${t}`}>
                          {t} <span className="text-[#6F828A]">· {n}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* ----- v77.99.57 — Request outcomes ----- */}
        <Card data-testid="insights-card-requests" style={{ borderTop: "3px solid #4A6170" }} className="border-[#D9E1E3] rounded-md shadow-none">
          <CardHeader className="border-b border-[#D9E1E3] flex flex-row items-center justify-between bg-[#F5F7F8]">
            <CardTitle className="font-heading text-base inline-flex items-center gap-2 min-w-0">
              <Inbox className="w-4 h-4 text-[#4A6170] flex-shrink-0" strokeWidth={1.6} /> Request outcomes
            </CardTitle>
            <span className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]" data-testid="insights-requests-window-label">
              {summaryWindow}d
            </span>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {!data.requests ? (
              <div className="text-sm text-[#6F828A]">{data.loading ? "Loading…" : "Nothing logged in this window yet."}</div>
            ) : data.requests.total === 0 ? (
              <div className="text-sm text-[#6F828A]" data-testid="insights-requests-empty">
                No co-parenting requests logged in this window.
              </div>
            ) : (
              <RequestOutcomes data={data.requests} />
            )}
          </CardContent>
        </Card>

        {/* ----- v77.99.57 — Schedule consistency ----- */}
        <Card data-testid="insights-card-schedule-consistency" style={{ borderTop: "3px solid #1F9FA4" }} className="border-[#D9E1E3] rounded-md shadow-none">
          <CardHeader className="border-b border-[#D9E1E3] flex flex-row items-center justify-between bg-[#F4FAFA]">
            <CardTitle className="font-heading text-base inline-flex items-center gap-2 min-w-0">
              <CalendarCheck className="w-4 h-4 text-[#116B6E] flex-shrink-0" strokeWidth={1.6} /> Schedule consistency
            </CardTitle>
            <span className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]" data-testid="insights-schedule-consistency-window-label">
              {summaryWindow}d
            </span>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {!data.summary ? (
              <div className="text-sm text-[#6F828A]">{data.loading ? "Loading…" : "Nothing logged in this window yet."}</div>
            ) : data.summary.schedule_consistency_pct == null ? (
              <div className="text-sm text-[#6F828A]" data-testid="insights-schedule-consistency-empty">
                No pickup, dropoff, or custody override events logged in this window.
              </div>
            ) : (
              <ScheduleConsistency
                pct={data.summary.schedule_consistency_pct}
                changes={data.summary.schedule_changes_count || 0}
              />
            )}
          </CardContent>
        </Card>

        {/* ----- v77.99.57 — Expense reimbursement balance ----- */}
        <Card data-testid="insights-card-reimbursement" style={{ borderTop: "3px solid #6F4E1E" }} className="border-[#D9E1E3] rounded-md shadow-none lg:col-span-2">
          <CardHeader className="border-b border-[#D9E1E3] flex flex-row items-center justify-between bg-[#FDFBF6]">
            <CardTitle className="font-heading text-base inline-flex items-center gap-2 min-w-0">
              <Wallet className="w-4 h-4 text-[#6F4E1E] flex-shrink-0" strokeWidth={1.6} /> Reimbursement balance
              {ChildScopeBadge}
            </CardTitle>
            <span className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">{expenseMonths}m</span>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {!data.reimburse ? (
              <div className="text-sm text-[#6F828A]">{data.loading ? "Loading…" : "Nothing logged in this window yet."}</div>
            ) : (data.reimburse.by_parent || []).length === 0 ? (
              <div className="text-sm text-[#6F828A]" data-testid="insights-reimburse-empty">
                No shared expense reimbursements recorded in this window.
              </div>
            ) : (
              <ReimbursementBalance data={data.reimburse} currency={currency} />
            )}
          </CardContent>
        </Card>

        {/* ----- v77.99.58 — Communication activity (timestamps only) ----- */}
        <Card data-testid="insights-card-communication" style={{ borderTop: "3px solid #4A6170" }} className="border-[#D9E1E3] rounded-md shadow-none lg:col-span-2">
          <CardHeader className="border-b border-[#D9E1E3] flex flex-row items-center justify-between bg-[#F5F7F8]">
            <CardTitle className="font-heading text-base inline-flex items-center gap-2 min-w-0">
              <Activity className="w-4 h-4 text-[#4A6170] flex-shrink-0" strokeWidth={1.6} /> Communication activity
            </CardTitle>
            <span className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">{summaryWindow}d</span>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            <p className="text-[11px] text-[#6F828A] leading-relaxed">
              Frequency only — no message content is read or surfaced. Times are in UTC.
            </p>
            {!data.communication ? (
              <div className="text-sm text-[#6F828A]">{data.loading ? "Loading…" : "Nothing logged in this window yet."}</div>
            ) : data.communication.total_messages === 0 ? (
              <div className="text-sm text-[#6F828A]" data-testid="insights-communication-empty">
                No messages logged in this window.
              </div>
            ) : (
              <CommunicationActivity data={data.communication} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// v77.99.58 — Communication activity heatmap. Pure timestamp visual:
// 7×24 day-of-week × hour-of-day grid coloured by message count
// (neutral teal ramp), an inline weekly volume sparkline-bar, and
// the four headline numbers. NO message content is ever rendered.
const HEATMAP_BG = ["#F4FAFA", "#D8EFEF", "#BDE3E3", "#86CDD0", "#4DA8AC", "#1F9FA4", "#116B6E"];
const DOW_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
function _maxIn(grid) {
  let m = 0;
  for (const row of grid) for (const v of row) if (v > m) m = v;
  return m;
}
function _bucket(v, max) {
  if (!v || max <= 0) return 0;
  const t = v / max;
  if (t < 0.05) return 1;
  if (t < 0.2) return 2;
  if (t < 0.4) return 3;
  if (t < 0.6) return 4;
  if (t < 0.8) return 5;
  return 6;
}
function CommunicationActivity({ data }) {
  const heatmap = data.heatmap || [];
  const max = _maxIn(heatmap);
  // Collapse 24 hours into 8 three-hour bins so the grid stays
  // readable on a 360–430px viewport without forcing horizontal
  // scroll. Mediator/lawyer doesn't need single-hour resolution; a
  // 3-hour block is plenty for "is this a late-night vs morning
  // pattern" reading.
  const bins = useMemo(() => {
    const out = [];
    for (let d = 0; d < 7; d++) {
      const row = [];
      for (let b = 0; b < 8; b++) {
        let v = 0;
        for (let h = b * 3; h < b * 3 + 3; h++) v += (heatmap[d] && heatmap[d][h]) || 0;
        row.push(v);
      }
      out.push(row);
    }
    return out;
  }, [heatmap]);
  const binsMax = _maxIn(bins);
  const weekly = data.weekly_totals || [];
  const weeklyMax = Math.max(1, ...weekly.map((w) => w.count));
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <SubStat label="Messages" value={data.total_messages} testid="insights-comm-total" />
        <SubStat label="Late-night (22–06)" value={data.late_night_count} testid="insights-comm-late-night" />
        <SubStat label="Avg response" value={data.avg_response_minutes == null ? "—" : `${Math.round(data.avg_response_minutes)}m`} testid="insights-comm-avg-response" />
        <SubStat label="Weeks tracked" value={weekly.length} testid="insights-comm-weeks" />
      </div>

      {/* 7×8 heatmap. Day-of-week down the side, 3-hour bins across. */}
      <div className="overflow-hidden">
        <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.08em] text-[#6F828A] pl-9 mb-1" aria-hidden="true">
          {["00", "03", "06", "09", "12", "15", "18", "21"].map((h) => (
            <div key={h} className="flex-1 text-center">{h}</div>
          ))}
        </div>
        {bins.map((row, di) => (
          <div key={DOW_LABELS[di]} className="flex items-center gap-1 mb-0.5" data-testid={`insights-comm-row-${DOW_LABELS[di].toLowerCase()}`}>
            <div className="w-8 text-[10px] uppercase tracking-[0.08em] text-[#6F828A]">{DOW_LABELS[di]}</div>
            {row.map((v, bi) => (
              <div
                key={`${DOW_LABELS[di]}-${bi}`}
                className="flex-1 h-5 rounded-sm"
                style={{ background: HEATMAP_BG[_bucket(v, binsMax)] }}
                title={`${DOW_LABELS[di]} ${(bi * 3).toString().padStart(2, "0")}:00–${((bi + 1) * 3).toString().padStart(2, "0")}:00 — ${v} message${v === 1 ? "" : "s"}`}
                aria-label={`${DOW_LABELS[di]} ${bi * 3}–${(bi + 1) * 3} hours: ${v} messages`}
              />
            ))}
          </div>
        ))}
        <div className="flex items-center gap-2 mt-2 text-[10px] text-[#6F828A]">
          <span>Less</span>
          {HEATMAP_BG.slice(1).map((c) => (
            <span key={c} className="inline-block w-4 h-3 rounded-sm" style={{ background: c }} />
          ))}
          <span>More</span>
          <span className="ml-auto tabular-nums">peak: {max} msg / hour</span>
        </div>
      </div>

      {weekly.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">Weekly volume</div>
          <div className="flex items-end gap-0.5 h-12" data-testid="insights-comm-weekly">
            {weekly.map((w, i) => {
              const h = Math.max(2, Math.round((w.count / weeklyMax) * 48));
              return (
                <div
                  key={w.week_start}
                  className={`flex-1 rounded-t-sm ${i === weekly.length - 1 ? "bg-[#116B6E]" : "bg-[#1F9FA4]"}`}
                  style={{ height: `${h}px` }}
                  title={`Week of ${w.week_start}: ${w.count} message${w.count === 1 ? "" : "s"}`}
                  aria-label={`Week of ${w.week_start}: ${w.count} messages`}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function SubStat({ label, value, testid }) {
  return (
    <div className="border border-[#EEF3F4] rounded-sm p-2 bg-white" data-testid={testid}>
      <div className="uppercase tracking-[0.1em] text-[10px] text-[#6F828A] leading-snug">{label}</div>
      <div className="font-heading text-lg sm:text-xl font-semibold tabular-nums text-[#0B2E32] mt-0.5">{value}</div>
    </div>
  );
}

// v77.99.57 — Stacked monthly bars + per-parent outstanding summary
// for the Reimbursement Balance card.
function ReimbursementBalance({ data, currency }) {
  const series = data.series || [];
  const hasAnyMovement = series.some((s) => s.fronted > 0 || s.reimbursed > 0 || s.outstanding > 0);
  return (
    <div className="space-y-3">
      <ul className="space-y-1.5 text-xs" data-testid="insights-reimbursement-parents">
        {(data.by_parent || []).map((p) => (
          <li key={p.parent_id} className="flex flex-wrap items-baseline gap-x-3 gap-y-1" data-testid={`insights-reimbursement-parent-${p.parent_id}`}>
            <span className="font-medium text-[#2E3A3F] flex-1 min-w-0 truncate">{p.name}</span>
            <span className="tabular-nums text-[#6F828A]">fronted <span className="text-[#0B2E32]">{money(p.fronted, data.currency || currency)}</span></span>
            <span className="tabular-nums text-[#6F828A]">reimbursed <span className="text-[#0F5658]">{money(p.reimbursed, data.currency || currency)}</span></span>
            <span className="tabular-nums text-[#6F828A]">outstanding <span className={p.outstanding > 0 ? "text-[#8C2A22] font-medium" : "text-[#6F828A]"}>{money(p.outstanding, data.currency || currency)}</span></span>
          </li>
        ))}
      </ul>
      {hasAnyMovement ? (
        <div className="h-[200px]">
          <ResponsiveContainer>
            <BarChart data={series} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF3F4" />
              <XAxis dataKey="month" tick={CHART_TICK} tickFormatter={fmtMonth} />
              <YAxis tick={CHART_TICK} />
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelFormatter={fmtMonth} formatter={(v) => money(v, data.currency || currency)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="reimbursed" stackId="a" fill="#0F5658" name="Reimbursed" />
              <Bar dataKey="outstanding" stackId="a" fill="#B88A3A" name="Outstanding" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="text-xs text-[#6F828A]" data-testid="insights-reimbursement-no-trend">
          No reimbursement movement in this window.
        </div>
      )}
    </div>
  );
}

// v77.99.57 — Compact stat tile for the top "Insight summary" strip.
function StatTile({ label, value, hint, testid }) {
  return (
    <div
      className="border border-[#EEF3F4] rounded-sm p-2.5 bg-white min-w-[140px] sm:min-w-0 flex-shrink-0 snap-start"
      data-testid={testid}
    >
      <div className="uppercase tracking-[0.1em] text-[10px] text-[#6F828A] leading-snug">{label}</div>
      <div className="font-heading text-xl sm:text-2xl font-semibold tabular-nums text-[#0B2E32] mt-0.5" data-testid={`${testid}-value`}>{value}</div>
      <div className="text-[10px] text-[#6F828A] mt-0.5">{hint}</div>
    </div>
  );
}

// v77.99.57 — Donut + segmented bar visual for the Request Outcomes card.
const REQUEST_OUTCOME_COLORS = {
  approved: "#0F5658",
  declined: "#8C2A22",
  pending: "#B88A3A",
  expired: "#6F828A",
  withdrawn: "#4A6170",
};

function RequestOutcomes({ data }) {
  const counts = data.counts || {};
  const slices = ["approved", "declined", "pending", "expired", "withdrawn"]
    .map((k) => ({ key: k, label: k.charAt(0).toUpperCase() + k.slice(1), value: counts[k] || 0 }))
    .filter((s) => s.value > 0);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <div className="font-heading text-2xl sm:text-3xl font-semibold tabular-nums text-[#0B2E32]" data-testid="insights-requests-response-rate">
          {data.response_rate_pct}%
        </div>
        <div className="text-xs text-[#6F828A]">
          decided · {data.avg_response_hours != null ? `avg ${data.avg_response_hours}h` : "no decisions yet"}
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 items-center">
        <div className="w-[160px] h-[160px] flex-shrink-0">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={slices} dataKey="value" nameKey="label" innerRadius={50} outerRadius={70} stroke="#fff" strokeWidth={2}>
                {slices.map((s) => <Cell key={s.key} fill={REQUEST_OUTCOME_COLORS[s.key]} />)}
              </Pie>
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <ul className="flex-1 min-w-0 w-full space-y-1.5 text-xs" data-testid="insights-requests-legend">
          {slices.map((s) => {
            const pct = data.total ? Math.round((s.value / data.total) * 100) : 0;
            return (
              <li key={s.key} className="flex items-center gap-2" data-testid={`insights-requests-legend-${s.key}`}>
                <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: REQUEST_OUTCOME_COLORS[s.key] }} />
                <span className="text-[#2E3A3F] flex-1 min-w-0 truncate">{s.label}</span>
                <span className="tabular-nums text-[#0B2E32] w-8 text-right">{s.value}</span>
                <span className="tabular-nums text-[#6F828A] w-10 text-right">{pct}%</span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

// v77.99.57 — Progress-ring style visual for Schedule consistency.
function ScheduleConsistency({ pct, changes }) {
  const circ = 2 * Math.PI * 36; // r=36
  const dashOffset = circ - (Math.max(0, Math.min(100, pct)) / 100) * circ;
  return (
    <div className="flex items-center gap-4">
      <div className="relative w-[96px] h-[96px] flex-shrink-0">
        <svg viewBox="0 0 96 96" className="w-full h-full -rotate-90">
          <circle cx="48" cy="48" r="36" fill="none" stroke="#EEF3F4" strokeWidth="10" />
          <circle
            cx="48"
            cy="48"
            r="36"
            fill="none"
            stroke="#0F5658"
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={circ}
            strokeDashoffset={dashOffset}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="font-heading text-xl font-semibold tabular-nums text-[#0B2E32]" data-testid="insights-schedule-consistency-pct">{pct}%</span>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-[#6F828A]">Completed as planned, against pickup, dropoff and override events recorded in the window.</div>
        <div className="text-[11px] text-[#6F828A] mt-1.5">
          <span className="font-medium text-[#2E3A3F]" data-testid="insights-schedule-consistency-changes">{changes}</span> change{changes === 1 ? "" : "s"} (cancellations + overrides)
        </div>
      </div>
    </div>
  );
}
